import React, { useState, useRef } from 'react';
import { Button, Input, Select, Card, notify } from './UI';
import { callGroqOCR, getLocalGroqKey, setLocalGroqKey } from '../services/groqService';
import { expensesApi, contactsApi } from '@/core/adapter-api';
import { UploadCloud, CheckCircle, AlertCircle, Loader, Type } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

// Hardcoded explicit prompt from finance-accounting/SKILL.md as requested:
const OCR_SYSTEM_PROMPT = `
Estás procesando (mediante GroqVision o equivalente local) el ticket o factura proporcionado.

REGLAS DE EXTRACCIÓN Y FISCALIDAD ESPAÑOLA:
Extrae los siguientes campos con precisión quirúrgica. Si algo no es legible, pon null.
- vendorName: Nombre del establecimiento (ej. "Repsol", "Mercadona", "AWS").
- vendorNif: NIF, CIF o DNI del proveedor si aparece. Si no se ve, pon null.
- totalAmount: Importe total a pagar (float)
- taxAmount: Importe del IVA (float). IMPORTANTE: Si es un ticket de restaurante o transporte en España el IVA suele ser 10%. Si es gasolina o electrónica, 21%.
- date: Fecha en formato YYYY-MM-DD.
- category: Elige ESTRICTAMENTE UNA de las siguientes categorías de gasto de autónomos: 
  ["DIETAS_Y_RESTAURANTES", "COMBUSTIBLE_Y_PEAJES", "MATERIAL_OFICINA", "SOFTWARE_Y_SERVICIOS_WEB", "TRANSPORTE_PUBLICO", "OTROS"]

Estructura tu respuesta EXCLUSIVAMENTE como un objeto JSON válido, sin delimitadores \`\`\`json ni texto adicional.
`;

export const ExpenseUploader = ({ onSaveSuccess }: { onSaveSuccess?: () => void }) => {
    const [apiKeyForm, setApiKeyForm] = useState(!getLocalGroqKey());
    const [groqKey, setGroqKey] = useState(getLocalGroqKey() || '');
    
    const [isDragging, setIsDragging] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [textInput, setTextInput] = useState('');
    const [mode, setMode] = useState<'image' | 'text'>('image');

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Form state from LLM
    const [formData, setFormData] = useState({
        vendorName: '',
        vendorNif: '',
        totalAmount: '',
        taxAmount: '',
        date: new Date().toISOString().split('T')[0],
        category: 'OTROS'
    });

    const categories = [
        "DIETAS_Y_RESTAURANTES", "COMBUSTIBLE_Y_PEAJES", "MATERIAL_OFICINA", 
        "SOFTWARE_Y_SERVICIOS_WEB", "TRANSPORTE_PUBLICO", "OTROS"
    ];

    const saveApiKey = () => {
        if (!groqKey.trim()) return notify('La API key no puede estar vacía', 'error');
        setLocalGroqKey(groqKey.trim());
        setApiKeyForm(false);
        notify('API key de Groq guardada', 'success');
    };

    const handleFile = async (file: File) => {
        if (!file.type.startsWith('image/')) {
            return notify('Solo se admiten imágenes (JPEG, PNG)', 'error');
        }
        
        setIsProcessing(true);
        try {
            // Convert to base64
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = async () => {
                let base64 = reader.result as string;
                // remove data:image/...;base64,
                const prefix = 'base64,';
                if (base64.includes(prefix)) {
                    base64 = base64.substring(base64.indexOf(prefix) + prefix.length);
                }

                await processOCR(base64, true);
            };
            reader.onerror = () => {
                notify('Error al leer la imagen', 'error');
                setIsProcessing(false);
            };
        } catch (e: any) {
            notify(e.message || 'Error desconocido', 'error');
            setIsProcessing(false);
        }
    };

    const processText = async () => {
        if (!textInput.trim()) return notify('Introduce texto del ticket', 'error');
        setIsProcessing(true);
        await processOCR(textInput, false);
    };

    const processOCR = async (content: string, isImage: boolean) => {
        try {
            let jsonString = '';
            if (isImage) {
                jsonString = await callGroqOCR(
                    OCR_SYSTEM_PROMPT,
                    "Por favor, analiza este ticket de gasto según las reglas de fiscalidad española indicadas.",
                    content
                );
            } else {
                jsonString = await callGroqOCR(
                    OCR_SYSTEM_PROMPT,
                    "Analiza el siguiente texto de un ticket de gasto:\\n" + content
                );
            }

            const parsed = JSON.parse(jsonString);
            
            setFormData({
                vendorName: parsed.vendorName || '',
                vendorNif: parsed.vendorNif || '',
                totalAmount: parsed.totalAmount ? String(parsed.totalAmount) : '',
                taxAmount: parsed.taxAmount ? String(parsed.taxAmount) : '',
                date: parsed.date || new Date().toISOString().split('T')[0],
                category: categories.includes(parsed.category) ? parsed.category : 'OTROS'
            });

            notify('Datos extraídos correctamente', 'success');
        } catch (error: any) {
            console.error('OCR Error:', error);
            notify('Error al procesar con IA: ' + (error.message || 'El JSON devuelto es inválido'), 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    const saveExpense = async () => {
        if (!formData.vendorName || !formData.totalAmount) {
            return notify('Faltan datos clave (Proveedor e Importe)', 'error');
        }

        try {
            // Find or create the supplier
            const allContacts = await contactsApi.all();
            const existingSupplier = allContacts.find((c: any) => 
                c.type === 'supplier' && 
                (
                  (formData.vendorNif && c.nif && c.nif.toLowerCase() === formData.vendorNif.toLowerCase()) || 
                  (!formData.vendorNif && c.name.toLowerCase() === formData.vendorName.toLowerCase())
                )
            );

            let finalSupplierId = '';
            if (existingSupplier) {
                finalSupplierId = existingSupplier.id;
            } else {
                finalSupplierId = uuidv4();
                const newSupplier = {
                    id: finalSupplierId,
                    createdAt: Date.now(),
                    name: formData.vendorName,
                    type: 'supplier',
                    nif: formData.vendorNif || '',
                    email: '',
                    phone: '',
                    address: '',
                    notes: 'Proveedor creado automáticamente vía OCR (Ticket/Factura).'
                };
                await contactsApi.add(newSupplier as any);
                notify('Nuevo proveedor creado: ' + formData.vendorName, 'info');
            }

            const total = parseFloat(formData.totalAmount);
            const vat = parseFloat(formData.taxAmount) || 0;
            const base = total - vat;
            
            const newExpense = {
                id: uuidv4(),
                createdAt: Date.now(),
                date: formData.date,
                supplierId: finalSupplierId,
                concept: 'Gasto en ' + formData.vendorName,
                category: formData.category,
                amount: base,
                vatPct: vat > 0 ? (vat / base) * 100 : 0,
                vatAmount: vat,
                total: total,
                paymentMethod: 'card', 
                attachment: mode === 'text' ? null : null,
                paid: true,
                deductible: true,
                updatedAt: Date.now()
            };

            await expensesApi.add(newExpense as any);
            notify('Ticket analizado y contabilizado correctamente', 'success');
            
            setFormData({ vendorName: '', vendorNif: '', totalAmount: '', taxAmount: '', date: new Date().toISOString().split('T')[0], category: 'OTROS' });
            setTextInput('');
            if (onSaveSuccess) onSaveSuccess();
        } catch (e: any) {
             notify('Error al guardar en base de datos: ' + e.message, 'error');
        }
    };

    if (apiKeyForm) {
        return (
            <Card className="p-6 max-w-lg mx-auto bg-blue-50/50 border-blue-100">
                <div className="flex items-start mb-4">
                    <AlertCircle className="text-blue-500 mr-3 mt-1" />
                    <div>
                        <h3 className="font-bold text-blue-900">Configuración de Inteligencia Financiera</h3>
                        <p className="text-sm text-blue-700 mt-1">Para usar la extracción OCR (Llama 3.2 Vision), necesitas configurar tu API Key gratuita de Groq.</p>
                    </div>
                </div>
                <Input 
                    label="Groq API Key (gsk_...)" 
                    type="password"
                    value={groqKey} 
                    onChange={(e: any) => setGroqKey(e.target.value)} 
                    placeholder="gsk_..."
                />
                <Button onClick={saveApiKey} className="mt-4 w-full">Guardar y Continuar</Button>
            </Card>
        );
    }

    return (
        <div className="space-y-6 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* UPLOADER / INPUT ZONE */}
                <Card className="p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-gray-800">Subir Ticket de Gasto</h3>
                        <div className="flex bg-gray-100 p-1 rounded-lg">
                            <button 
                                onClick={() => setMode('image')} 
                                className={`px-3 py-1 text-sm rounded-md transition-all ${mode === 'image' ? 'bg-white shadow-sm font-bold' : 'text-gray-500'}`}
                            >
                                <UploadCloud size={16} className="inline mr-1" /> Foto
                            </button>
                            <button 
                                onClick={() => setMode('text')} 
                                className={`px-3 py-1 text-sm rounded-md transition-all ${mode === 'text' ? 'bg-white shadow-sm font-bold' : 'text-gray-500'}`}
                            >
                                <Type size={16} className="inline mr-1" /> Texto
                            </button>
                        </div>
                    </div>

                    {mode === 'image' ? (
                        <div 
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={onDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${isDragging ? 'border-[var(--accent-blue)] bg-[var(--accent-blue-soft)]' : 'border-gray-300 hover:border-gray-400 bg-gray-50'}`}
                        >
                            <input 
                                type="file" 
                                ref={fileInputRef} 
                                className="hidden" 
                                accept="image/jpeg, image/png, image/webp"
                                onChange={(e) => e.target.files && handleFile(e.target.files[0])}
                            />
                            {isProcessing ? (
                                <div className="text-[var(--accent-blue)] flex flex-col items-center">
                                    <Loader className="animate-spin mb-2" size={32} />
                                    <p className="font-medium animate-pulse">Llama 3.2 Vision analizando...</p>
                                </div>
                            ) : (
                                <div className="text-gray-500">
                                    <UploadCloud size={40} className="mx-auto mb-3 text-gray-400" />
                                    <p className="font-medium text-gray-700">Arrastra una foto del ticket</p>
                                    <p className="text-xs mt-1">o haz clic para seleccionar (JPEG, PNG)</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <textarea 
                                className="w-full border border-gray-300 rounded-lg p-3 text-sm h-32 outline-none focus:ring-2 focus:ring-[var(--accent-blue)]"
                                placeholder="Pega aquí el texto del recibo o factura para extraer datos con IA..."
                                value={textInput}
                                onChange={(e) => setTextInput(e.target.value)}
                            />
                            <Button 
                                onClick={processText} 
                                disabled={isProcessing || !textInput.trim()}
                                className="w-full"
                            >
                                {isProcessing ? <><Loader size={16} className="animate-spin mr-2" /> Analizando...</> : 'Extraer Datos'}
                            </Button>
                        </div>
                    )}
                </Card>

                {/* RESULT FORM ZONE */}
                <Card className="p-6 bg-gradient-to-br from-white to-gray-50/50">
                    <div className="flex items-center mb-5 text-[var(--accent-blue)] border-b pb-3">
                        <CheckCircle size={20} className="mr-2" />
                        <h3 className="font-bold">Datos Autocompletados (CoT Extraídos)</h3>
                    </div>
                    
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <Input 
                                label="Proveedor / Establecimiento" 
                                value={formData.vendorName} 
                                onChange={(e: any) => setFormData({...formData, vendorName: e.target.value})} 
                                placeholder="Ej. Mercadona"
                            />
                            <Input 
                                label="NIF/CIF (Opcional)" 
                                value={formData.vendorNif} 
                                onChange={(e: any) => setFormData({...formData, vendorNif: e.target.value})} 
                                placeholder="Ej. B12345678"
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <Input 
                                label="Total (€)" 
                                type="number" 
                                step="0.01"
                                value={formData.totalAmount} 
                                onChange={(e: any) => setFormData({...formData, totalAmount: e.target.value})} 
                            />
                            <Input 
                                label="IVA (€) Estimado" 
                                type="number" 
                                step="0.01"
                                value={formData.taxAmount} 
                                onChange={(e: any) => setFormData({...formData, taxAmount: e.target.value})} 
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <Input 
                                label="Fecha" 
                                type="date" 
                                value={formData.date} 
                                onChange={(e: any) => setFormData({...formData, date: e.target.value})} 
                            />
                            <Select 
                                label="Categoría (España)" 
                                value={formData.category} 
                                onChange={(e: any) => setFormData({...formData, category: e.target.value})}
                            >
                                {categories.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                            </Select>
                        </div>
                    </div>

                    <div className="mt-6">
                        <Button 
                            variant="primary" 
                            className="w-full font-bold shadow-md hover:shadow-lg"
                            onClick={saveExpense}
                            disabled={isProcessing}
                        >
                            Confirmar y Contabilizar Gasto
                        </Button>
                    </div>
                </Card>

            </div>
        </div>
    );
};
